import { useEffect, useRef, useState } from "react";
import { Users, Factory, Calendar, Weight, TrendingUp, MapPin } from "lucide-react";

const stats = [
  { icon: Users, value: 10000, suffix: "+", label: "Farmers Served", prefix: "" },
  { icon: Factory, value: 4, suffix: "", label: "Manufacturing Plants", prefix: "" },
  { icon: Calendar, value: 40, suffix: "+", label: "Years Experience", prefix: "" },
  { icon: Weight, value: 365000, suffix: "+", label: "Tonnes Annual Production Capacity", prefix: "" },
  { icon: TrendingUp, value: 98, suffix: "%", label: "Customer Retention", prefix: "" },
  { icon: MapPin, value: 20, suffix: "+", label: "States Served", prefix: "" },
];

const useCountUp = (end: number, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start]);
  return count;
};

const StatItem = ({ icon: Icon, value, suffix, label, prefix, inView }: {
  icon: any; value: number; suffix: string; label: string; prefix: string; inView: boolean;
}) => {
  const count = useCountUp(value, 2000, inView);
  return (
    <div className="flex flex-col items-center text-center group">
      <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
        <Icon className="h-7 w-7 text-accent" />
      </div>
      <span className="text-3xl md:text-4xl font-bold font-display text-primary">
        {prefix}{count.toLocaleString()}{suffix}
      </span>
      <span className="text-sm text-muted-foreground font-body mt-1">{label}</span>
    </div>
  );
};

const StatsCounter = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-12">
          {stats.map((stat, i) => (
            <StatItem key={i} {...stat} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsCounter;
